import type { ReconData } from "../data/liveApi";
import { pressureDeltaColor, arrow } from "../engine/intensity";

// ── Hurricane-hunter reconnaissance summary ──
// The vortex messages are the MEASURED central pressure — the number the
// advisories and the best track are built from. Shown newest first with the
// fix-to-fix pressure change, plus the strongest winds the aircraft found.

const zt = (iso: string) => `${iso.slice(5, 10).replace("-", "/")} ${iso.slice(11, 16)}Z`;

export default function ReconPanel({ recon, show, onToggle }: { recon: ReconData | null; show: boolean; onToggle: () => void }) {
  if (!recon) return null;
  const { fixes, flights } = recon;
  const hours = Math.round((Date.parse(recon.until) - Date.parse(recon.since)) / 3_600_000);

  if (!fixes.length && !flights.length) {
    return (
      <div className="muted xs" style={{ marginTop: 14, textTransform: "none", letterSpacing: 0 }}>
        ✈️ No hurricane-hunter missions into this storm in the last {hours} h.
      </div>
    );
  }

  const latest = fixes[fixes.length - 1];
  // strongest winds any aircraft measured in the window
  let peakSfmr: { kt: number; t: string; ac: string } | null = null;
  let peakFl: { kt: number; t: string; ac: string } | null = null;
  for (const f of flights) {
    for (const o of f.obs) {
      if (o.sfmr_kt != null && (!peakSfmr || o.sfmr_kt > peakSfmr.kt)) peakSfmr = { kt: o.sfmr_kt, t: o.t, ac: f.aircraft };
      if (o.fl_kt != null && (!peakFl || o.fl_kt > peakFl.kt)) peakFl = { kt: o.fl_kt, t: o.t, ac: f.aircraft };
    }
  }
  const rows = [...fixes].reverse().slice(0, 6);

  return (
    <>
      <div className="muted xs" style={{ marginTop: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span>✈️ HURRICANE HUNTERS · {flights.length} MISSION{flights.length === 1 ? "" : "S"} · {fixes.length} FIX{fixes.length === 1 ? "" : "ES"}</span>
        <button className={show ? "mini-toggle on" : "mini-toggle"} onClick={onToggle} title="Show the flight tracks and center fixes on the map">
          map: {show ? "on" : "off"}
        </button>
      </div>
      {latest && (
        <div className="recon-latest">
          <div>
            Latest center fix <b>{zt(latest.t)}</b>{latest.aircraft ? ` · ${latest.aircraft}` : ""}
          </div>
          <div style={{ marginTop: 2 }}>
            <span className="recon-mb">{latest.mslp ?? "—"} mb</span>{" "}
            <span className="muted">{latest.extrap ? "extrapolated from flight level" : "dropsonde"}</span>
          </div>
          <div className="muted" style={{ marginTop: 2 }}>
            {latest.eye ? `Eye ${latest.eye}${latest.eye_nm ? ` (${latest.eye_nm} nm)` : ""}` : "No eye reported"}
            {latest.max_fl_kt != null && ` · max flight-level ${latest.max_fl_kt} kt`}
          </div>
        </div>
      )}
      {fixes.length > 1 && (
        <table className="adv-table">
          <thead>
            <tr><th>FIX</th><th className="num">MSLP</th><th>EYE</th><th className="num">FL kt</th></tr>
          </thead>
          <tbody>
            {rows.map((f, i) => {
              const prev = fixes[fixes.length - 2 - i];
              const dp = prev && f.mslp != null && prev.mslp != null ? f.mslp - prev.mslp : null;
              return (
                <tr key={`${f.t}-${f.mission}`}>
                  <td>{zt(f.t)}</td>
                  <td className="num">
                    {f.mslp ?? "—"}{f.extrap && <span className="muted" title="extrapolated from flight level">*</span>}
                    {dp != null && dp !== 0 && <span className="dd" style={{ color: pressureDeltaColor(dp) }}>{arrow(dp)}</span>}
                  </td>
                  <td className="muted">{f.eye ?? ""}</td>
                  <td className="num">{f.max_fl_kt ?? ""}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {(peakSfmr || peakFl) && (
        <div className="muted xs" style={{ marginTop: 4, textTransform: "none", letterSpacing: 0, lineHeight: 1.5 }}>
          Strongest measured: {peakSfmr && <>SFMR surface <b>{peakSfmr.kt} kt</b> ({peakSfmr.ac} {peakSfmr.t.slice(11, 16)}Z)</>}
          {peakSfmr && peakFl && " · "}
          {peakFl && <>flight-level <b>{peakFl.kt} kt</b></>}
          . * = extrapolated pressure. Map dots: SFMR surface wind (faint = flight-level only).
        </div>
      )}
    </>
  );
}
